// The offline transport (BL-2343, ADR 066 — Offline Mode § 3): a `fetch` the typed API client
// uses while the phone app is offline. It answers the supported registry routes from the device
// store and fails every other request the way a dropped connection does.
import { createRouteMatcher } from '@auctogon/core/api/registry';
import type { InferResponse, RegistryTree, RouteEntry } from '@auctogon/core/api/registry';

import type { OfflineStore } from '../../gateways/native-device';
import { NOT_FOUND } from './offline-answers';

/** What a handler is given: the matched route's params, the query, the JSON body, and the store. */
export interface OfflineRequest {
  params: Record<string, string>;
  query: URLSearchParams;
  body: unknown;
  /**
   * The active scope's store, or null when the device has no signed-in scope. Opened on the
   * handler's first call and shared for the rest of the request, so a handler that reads no
   * records opens nothing.
   */
  openStore: () => Promise<OfflineStore | null>;
}

/**
 * A handler's answer, typed against the route's own response DTO (ADR 027 — Response Contract).
 * - `ok` — the standard `{ success: true, data }` envelope.
 * - `raw` — a route the client reads through `.raw()` with a non-envelope body (`auth.me`).
 * - `error` — the standard error envelope at a status, as the server would send it.
 */
export type OfflineAnswer<R, Raw = never> =
  | { kind: 'ok'; data: R }
  | { kind: 'raw'; status: number; body: Raw }
  | { kind: 'error'; status: number; error: string };

/** One route's offline answer. */
export type OfflineHandler<R, Raw = never> = (request: OfflineRequest) => Promise<OfflineAnswer<R, Raw>>;

type EntryAt<T, Path extends string> = Path extends `${infer Head}.${infer Rest}`
  ? Head extends keyof T
    ? EntryAt<T[Head], Rest>
    : never
  : Path extends keyof T
    ? T[Path]
    : never;

/**
 * The response DTO the registry declares for `"<dotted.key>:<VERB>"`, so a handler that returns
 * any other shape fails `tsc`.
 */
export type OfflineResponseFor<Registry, Key extends string> = Key extends `${infer Path}:${infer Verb}`
  ? EntryAt<Registry, Path> extends RouteEntry<infer _P, infer Methods>
    ? Verb extends keyof Methods
      ? InferResponse<Methods[Verb]>
      : never
    : never
  : never;

/** A route key the transport answers: `"<dotted.key>:<VERB>"`. */
export type OfflineRouteKey = `${string}:${string}`;

/** The handler table: one per supported route key. */
export type OfflineHandlerTable = Record<OfflineRouteKey, OfflineHandler<unknown, unknown>>;

/** Inputs to {@link createOfflineTransport}. */
export interface OfflineTransportOptions {
  registry: RegistryTree;
  handlers: OfflineHandlerTable;
  /** The active scope's store, or null when none is signed in. Called per request. */
  openStore: () => Promise<OfflineStore | null>;
}

/** The rejection a dropped connection produces, so every screen's network-error path renders. */
function networkFailure(): TypeError {
  return new TypeError('Failed to fetch');
}

function toUrl(input: RequestInfo | URL): URL {
  if (input instanceof URL) return input;
  const href = typeof input === 'string' ? input : input.url;
  // The base only resolves the relative URLs the typed client issues; it is never fetched.
  return new URL(href, 'http://offline.invalid');
}

function readBody(body: BodyInit | null | undefined): unknown {
  if (typeof body !== 'string') return undefined;
  try {
    return JSON.parse(body);
  } catch {
    // The typed client JSON-stringifies every object body, so a non-JSON string is a raw body no
    // handler reads. It reaches the handler as undefined, by design.
    return undefined;
  }
}

function json(status: number, body: unknown): Response {
  return new Response(JSON.stringify(body), { status, headers: { 'Content-Type': 'application/json' } });
}

function toResponse(answer: OfflineAnswer<unknown, unknown>): Response {
  if (answer.kind === 'ok') return json(200, { success: true, data: answer.data });
  if (answer.kind === 'raw') return json(answer.status, answer.body);
  return json(answer.status, { success: false, error: answer.error });
}

/**
 * The tenant a request names: its `tenantId` query, or the `:id` of a `/api/tenants/:id/…` route.
 * The store answers only for its own scope's tenant (ADR 066 — Offline Mode § 2), and right after
 * an offline tenant switch the screens read before the active-scope pointer moves (BUG-2064).
 */
function namedTenant(input: { key: string; params: Record<string, string>; query: URLSearchParams }): string | null {
  if (input.key.startsWith('tenants.id.')) return input.params.id ?? null;
  return input.query.get('tenantId');
}

/**
 * Build the offline transport.
 *
 * @param options - the registry, the handler table and how to open the active scope's store.
 * @returns a `fetch` that answers handled routes from the store, 404s a request naming a tenant
 *   other than the store's (BUG-2064), and rejects everything else as a network failure (ADR 066 —
 *   Offline Mode § 3).
 */
export function createOfflineTransport(options: OfflineTransportOptions): typeof fetch {
  const match = createRouteMatcher(options.registry);
  return async (input, init) => {
    const url = toUrl(input);
    const verb = init?.method ?? (input instanceof Request ? input.method : 'GET');
    const matched = match({ verb, pathname: url.pathname });
    const handler = matched ? options.handlers[`${matched.key}:${matched.verb}`] : undefined;
    if (!matched || !handler) throw networkFailure();
    let store: Promise<OfflineStore | null> | null = null;
    const openStore = () => (store ??= options.openStore());
    const tenantId = namedTenant({ key: matched.key, params: matched.params, query: url.searchParams });
    if (tenantId != null) {
      const scoped = await openStore();
      // Another tenant's request is a record this store does not hold, never the active one's.
      if (scoped && scoped.scope.tenantId !== tenantId) return toResponse(NOT_FOUND);
    }
    const answer = await handler({ params: matched.params, query: url.searchParams, body: readBody(init?.body), openStore });
    return toResponse(answer);
  };
}
